import { FC } from 'react';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@new/lib';
import type { UserCourse } from '@teachur/http-client';

interface StatusBadgeProps {
  userCourse: UserCourse;
  showPassedDate?: boolean;
}

type CourseStatusKey = 'passed' | 'inProgress' | 'notStarted';

interface CourseStatusConfig {
  label: string;
  icon: LucideIcon;
  className: string;
}

const COURSE_STATUS_CONFIG: Record<CourseStatusKey, CourseStatusConfig> = {
  passed: {
    label: 'Passed',
    icon: CheckCircle,
    className: 'text-(--success-S700) bg-(--success-S200) border-(--success-S300)',
  },
  inProgress: {
    label: 'In Progress',
    icon: Clock,
    className: 'text-(--primary-O1100) bg-(--primary-O200) border-(--primary-O300)',
  },
  notStarted: {
    label: 'Not Started',
    icon: XCircle,
    className:
      'text-(--neutral-black-NB700) bg-(--neutral-black-NB100) border-(--neutral-black-NB300)',
  },
};

const COURSE_STATUS_BADGE_CLASS =
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium leading-tight';

const COURSE_DATE_BADGE_CLASS =
  'inline-flex items-center rounded-full border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) px-2 py-0.5 text-xs font-medium text-(--neutral-black-NB700) shadow-sm';

const COURSE_STATUS_ICON_CLASS = 'h-3 w-3';

const formatPassedDate = (passedAt?: string | null) => {
  if (!passedAt) return null;

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(passedAt));
};

const getCourseStatusKey = (userCourse: UserCourse): CourseStatusKey => {
  if (userCourse.passed) return 'passed';
  if (userCourse.started) return 'inProgress';

  return 'notStarted';
};

export const StatusBadge: FC<StatusBadgeProps> = ({ userCourse, showPassedDate = false }) => {
  const statusKey = getCourseStatusKey(userCourse);
  const { label, icon: Icon, className } = COURSE_STATUS_CONFIG[statusKey];

  const passedDate =
    showPassedDate && userCourse.passed ? formatPassedDate(userCourse.passedAt) : null;

  return (
    <div className="flex items-center gap-2">
      <span className={cn(COURSE_STATUS_BADGE_CLASS, className)}>
        <Icon className={COURSE_STATUS_ICON_CLASS} />
        {label}
      </span>

      {passedDate && <span className={COURSE_DATE_BADGE_CLASS}>{passedDate}</span>}
    </div>
  );
};
